import type { ReactElement, ReactNode } from 'react';
import { cn } from '../lib/utils';

export interface AppShellProps {
  readonly header?: ReactNode;
  /** 사이드바 영역 — 폭·접힘·반응형 스타일은 전달된 노드가 스스로 책임진다. */
  readonly sidebar?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  /** 본문(main)에 더할 클래스 — 본문 바탕색·여백처럼 앱마다 다른 것. */
  readonly mainClassName?: string;
  /**
   * true 면 셸이 뷰포트 높이에 고정되고 본문(main)만 스크롤된다 —
   * 헤더·사이드바가 항상 화면에 남는 어드민형 레이아웃.
   * false(기본)는 문서 전체가 스크롤되는 일반 레이아웃.
   */
  readonly lockViewport?: boolean;
}

/**
 * (선택) 사이드바 + 헤더 + 본문으로 구성된 애플리케이션 레이아웃.
 * 사이드바는 화면 높이를 다 차지하고, 헤더는 그 옆 칸에서 본문 위에 놓인다(사이드바 위에 로고를 둘 수 있게).
 */
export const AppShell = ({
  header,
  sidebar,
  children,
  className,
  mainClassName,
  lockViewport = false,
}: AppShellProps): ReactElement => (
  <div className={cn('flex bg-background', lockViewport ? 'h-dvh' : 'min-h-dvh', className)}>
    {sidebar}

    <div className={cn('flex min-w-0 flex-1 flex-col', lockViewport && 'min-h-0')}>
      {header}

      <main
        className={cn(
          'min-w-0 flex-1 p-6',
          // overflow 는 기준 블록이 뷰포트이거나 자신의 조상인 자손은 가두지 못한다(CSS 2.1 §11.1.1).
          // main 이 positioned 가 아니면 sr-only(absolute) 자손의 기준 블록이 뷰포트가 되어 문서 높이를
          // 늘리고 셸 밖 문서 전체가 스크롤된다 — relative 로 main 을 기준 블록 체인에 넣는다.
          lockViewport && 'relative overflow-y-auto',
          mainClassName,
        )}
      >
        {children}
      </main>
    </div>
  </div>
);
